import { PublicShell } from "@/components/layouts/PublicShell";
import { Skeleton } from "@/components/ui/skeleton";
import { CatalogCardSkeleton } from "@/components/marketing/CatalogCardSkeleton";

/** Shared header shimmer used by list pages. */
function HeaderShimmer() {
  return (
    <div className="mb-8 space-y-3">
      <Skeleton className="h-9 w-2/3 max-w-md" />
      <Skeleton className="h-4 w-1/2 max-w-sm" />
    </div>
  );
}

/* ============================ Bootcamps grid ============================ */
export function BootcampGridSkeleton() {
  return (
    <PublicShell>
      <div className="container py-12">
        <div className="grid gap-8 lg:grid-cols-[260px_1fr]">
          <div className="hidden space-y-4 lg:block">
            <Skeleton className="h-6 w-24" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-32 w-full" />
          </div>
          <div>
            <Skeleton className="mb-4 h-5 w-32" />
            <div className="grid gap-6 sm:grid-cols-2 xl:grid-cols-3">
              {Array.from({ length: 6 }).map((_, i) => <CatalogCardSkeleton key={i} />)}
            </div>
          </div>
        </div>
      </div>
    </PublicShell>
  );
}

/* ============================ Mentor grid ============================ */
function MentorCardSkeleton() {
  return (
    <div className="rounded-xl border border-border bg-card p-5">
      <div className="flex gap-4">
        <Skeleton className="h-16 w-16 shrink-0 rounded-full" />
        <div className="min-w-0 flex-1 space-y-2">
          <Skeleton className="h-5 w-3/4" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-2/3" />
        </div>
      </div>
      <div className="mt-3 flex flex-wrap gap-1.5">
        <Skeleton className="h-5 w-16" />
        <Skeleton className="h-5 w-20" />
        <Skeleton className="h-5 w-14" />
      </div>
      <Skeleton className="mt-4 h-4 w-24" />
    </div>
  );
}

export function MentorsGridBody() {
  return (
    <div className="grid gap-4 sm:grid-cols-2">
      {Array.from({ length: 6 }).map((_, i) => <MentorCardSkeleton key={i} />)}
    </div>
  );
}

export function MentorsGridSkeleton() {
  return (
    <PublicShell>
      <main className="container mx-auto max-w-6xl px-4 py-10">
        <HeaderShimmer />
        <div className="grid gap-6 md:grid-cols-[240px_1fr]">
          <aside className="hidden space-y-4 md:block">
            <Skeleton className="h-5 w-24" />
            <Skeleton className="h-6 w-full" />
            <Skeleton className="h-6 w-full" />
            <Skeleton className="h-6 w-full" />
            <Skeleton className="mt-6 h-5 w-24" />
            <Skeleton className="h-6 w-full" />
            <Skeleton className="h-6 w-full" />
          </aside>
          <section><MentorsGridBody /></section>
        </div>
      </main>
    </PublicShell>
  );
}

/* ============================ Mentor detail ============================ */
export function MentorDetailSkeleton() {
  return (
    <PublicShell>
      <main className="container mx-auto max-w-5xl px-4 py-10">
        <div className="flex flex-col gap-6 md:flex-row">
          <Skeleton className="h-32 w-32 shrink-0 rounded-full" />
          <div className="flex-1 space-y-3">
            <Skeleton className="h-8 w-2/3" />
            <Skeleton className="h-4 w-1/2" />
            <div className="flex gap-2">
              <Skeleton className="h-6 w-20" />
              <Skeleton className="h-6 w-20" />
              <Skeleton className="h-6 w-20" />
            </div>
          </div>
        </div>
        <div className="mt-10 grid gap-8 md:grid-cols-[1fr_320px]">
          <div className="space-y-3">
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-5/6" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-3/4" />
          </div>
          <div className="space-y-3 rounded-xl border border-border p-4">
            <Skeleton className="h-5 w-32" />
            <div className="grid grid-cols-3 gap-2">
              {Array.from({ length: 9 }).map((_, i) => <Skeleton key={i} className="h-9" />)}
            </div>
          </div>
        </div>
      </main>
    </PublicShell>
  );
}

/* ============================ Blog page ============================ */
export function BlogPageSkeleton() {
  return (
    <PublicShell>
      <section className="container pt-12 pb-8 md:pt-16">
        <div className="mx-auto max-w-3xl space-y-4 text-center">
          <Skeleton className="mx-auto h-3 w-32" />
          <Skeleton className="mx-auto h-10 w-3/4" />
          <Skeleton className="mx-auto h-4 w-2/3" />
        </div>
      </section>
      <section className="container pb-8">
        <div className="mx-auto max-w-4xl space-y-4">
          <Skeleton className="h-11 w-full" />
          <div className="flex flex-wrap justify-center gap-2">
            {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-7 w-20 rounded-full" />)}
          </div>
        </div>
      </section>
      <section className="container pb-16">
        <div className="grid gap-10 lg:grid-cols-[1fr_300px]">
          <div className="space-y-6">
            <Skeleton className="h-80 w-full rounded-xl" />
            <div className="grid gap-6 sm:grid-cols-2">
              {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-64 rounded-xl" />)}
            </div>
          </div>
          <aside className="space-y-3">
            <Skeleton className="h-5 w-32" />
            {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-16 w-full" />)}
          </aside>
        </div>
      </section>
    </PublicShell>
  );
}

/* ============================ Community feed ============================ */
function PostRowSkeleton() {
  return (
    <div className="rounded-md border border-border bg-card p-4">
      <div className="flex items-center gap-2">
        <Skeleton className="h-6 w-16 rounded-full" />
        <Skeleton className="h-3 w-24" />
      </div>
      <Skeleton className="mt-3 h-5 w-3/4" />
      <Skeleton className="mt-2 h-4 w-full" />
      <Skeleton className="mt-1 h-4 w-2/3" />
      <div className="mt-3 flex gap-3">
        <Skeleton className="h-4 w-12" />
        <Skeleton className="h-4 w-12" />
      </div>
    </div>
  );
}

export function CommunityFeedBody() {
  return (
    <div className="space-y-3">
      {Array.from({ length: 6 }).map((_, i) => <PostRowSkeleton key={i} />)}
    </div>
  );
}

export function CommunityFeedSkeleton() {
  return (
    <PublicShell>
      <section className="container py-8">
        <div className="mb-6 flex flex-wrap items-center justify-between gap-2">
          <div className="space-y-2">
            <Skeleton className="h-8 w-40" />
            <Skeleton className="h-4 w-64" />
          </div>
          <Skeleton className="h-9 w-28" />
        </div>
        <div className="mb-6 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <Skeleton className="h-10 max-w-sm flex-1" />
          <div className="flex gap-2">
            <Skeleton className="h-10 w-40" />
            <Skeleton className="h-10 w-40" />
          </div>
        </div>
        <CommunityFeedBody />
      </section>
    </PublicShell>
  );
}

/* ============================ Case studies grid ============================ */
function CaseStudyCardSkeleton() {
  return (
    <div className="overflow-hidden rounded-xl border border-border bg-card">
      <Skeleton className="h-40 w-full rounded-none" />
      <div className="space-y-3 p-5">
        <Skeleton className="h-3 w-1/2" />
        <Skeleton className="h-5 w-3/4" />
        <Skeleton className="h-4 w-2/3" />
        <Skeleton className="h-3 w-24" />
      </div>
    </div>
  );
}

export function CaseStudiesGridSkeleton() {
  return (
    <PublicShell>
      <section className="container py-12 md:py-16">
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 6 }).map((_, i) => <CaseStudyCardSkeleton key={i} />)}
        </div>
      </section>
    </PublicShell>
  );
}

/* ============================ Case study detail ============================ */
export function CaseStudyDetailSkeleton() {
  return (
    <PublicShell>
      <article className="container max-w-4xl py-12">
        <Skeleton className="mb-6 h-4 w-32" />
        <Skeleton className="h-10 w-3/4" />
        <Skeleton className="mt-3 h-5 w-1/2" />
        <div className="mt-4 flex gap-2">
          <Skeleton className="h-6 w-20" />
          <Skeleton className="h-6 w-20" />
        </div>
        <Skeleton className="mt-8 h-64 w-full rounded-xl" />
        <div className="mt-8 space-y-3">
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-5/6" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-2/3" />
          <Skeleton className="mt-6 h-4 w-full" />
          <Skeleton className="h-4 w-3/4" />
        </div>
      </article>
    </PublicShell>
  );
}